import React, { useState } from 'react';
import { Blaetterleiste, TabellenKopf, useTabelle, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, CheckCircle, Rocket } from 'lucide-react';
import { releases, applications, registry } from '../api';
import type { ProduktBild } from '../api';
import StatusBadge from '../components/StatusBadge';
import type { ReleaseStatus } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { useEinstellung } from '../contexts/EinstellungenContext';

// Ein Release gehoert seit Stufe 2 der Application: es gilt fuer alle ihre
// Instanzen, gleich ueber welches Produkt sie gekauft wurden.
interface ReleaseForm {
  applicationId: string;
  version: string;
  releaseName: string;
  releaseChannel: string;
  backendImage: string;
  backendImageTag: string;
  frontendImage: string;
  frontendImageTag: string;
  workerImage: string;
  workerImageTag: string;
  /** Bildtags je Vorlagenvariable. Kennt beliebig viele Images. */
  imageTags: Record<string, string>;
  releaseNotes: string;
  databaseMigrationRequired: boolean;
  breakingChange: boolean;
}

const DEFAULT_FORM: ReleaseForm = {
  applicationId: '',
  version: '',
  releaseName: '',
  releaseChannel: 'stable',
  backendImage: '',
  backendImageTag: '',
  frontendImage: '',
  frontendImageTag: '',
  workerImage: '',
  workerImageTag: '',
  imageTags: {},
  releaseNotes: '',
  databaseMigrationRequired: false,
  breakingChange: false,
};

/**
 * Die eigenen Images einer Application, je eines mit Tag.
 *
 * Gelesen wird aus der Vorlage, nicht aus einer Liste im Code. Das ist der
 * Punkt: ein neuer Container steht in der compose.yaml, und damit steht er
 * hier — ohne dass jemand losgeschickt werden muss, um ein Feld zu ergänzen.
 */
function ReleaseBilder({
  applicationId,
  werte,
  onChange,
}: {
  applicationId: string;
  werte: Record<string, string>;
  onChange: (schluessel: string, wert: string) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['application-images', applicationId],
    queryFn: () => applications.images(applicationId),
    enabled: Boolean(applicationId),
  });

  if (!applicationId || isLoading) return null;

  if (!data?.template) {
    return (
      <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
        {t('releaseImages.noTemplate')}
      </div>
    );
  }
  if (!data.images.length) {
    return (
      <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
        {t('releaseImages.none')}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-700">{t('releaseImages.title')}</span>
        <span className="text-xs text-slate-400">
          {t('releaseImages.fromTemplate', { version: data.template.version })}
        </span>
      </div>
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {data.images.map((b) => (
          <ReleaseBildZeile
            key={b.schluessel}
            bild={b}
            wert={werte[b.schluessel] ?? ''}
            onChange={(v) => onChange(b.schluessel, v)}
          />
        ))}
      </div>
    </div>
  );
}

function ReleaseBildZeile({
  bild,
  wert,
  onChange,
}: {
  bild: ProduktBild;
  wert: string;
  onChange: (wert: string) => void;
}) {
  const { t } = useTranslation();
  // Erst laden, wenn jemand die Auswahl anfasst: jeder Tag kostet die Registry
  // zwei Anfragen, und die meisten Formulare werden ohne Blick darauf
  // abgeschickt.
  const [offen, setOffen] = React.useState(false);
  const tags = useQuery({
    queryKey: ['registry-tags', bild.repository],
    queryFn: () => registry.tags(bild.repository, true),
    enabled: offen,
    staleTime: 60_000,
  });

  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-slate-700">{bild.dienst}</div>
        <div className="truncate font-mono text-[11px] text-slate-400">{bild.repository}</div>
      </div>
      <select
        value={wert}
        onFocus={() => setOffen(true)}
        onChange={(e) => onChange(e.target.value)}
        className="w-56 rounded border border-slate-300 px-1.5 py-1 font-mono text-xs"
      >
        <option value="">— {t('releaseImages.empty')}</option>
        {tags.data?.tags.map((x) => (
          <option key={x.tag} value={x.tag}>
            {x.tag}
            {x.createdAt ? `  ·  ${x.createdAt.slice(0, 10)}` : ''}
          </option>
        ))}
        {/* Ein bereits gesetzter Tag, den die Registry nicht (mehr) fuehrt,
            darf nicht aus der Auswahl fallen — sonst zeigt das Feld etwas
            anderes an, als gespeichert wird. */}
        {wert && !tags.data?.tags.some((x) => x.tag === wert) && (
          <option value={wert}>{wert}</option>
        )}
      </select>
    </div>
  );
}

export default function ReleasesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<ReleaseForm>(DEFAULT_FORM);
  const [formError, setFormError] = useState('');
  const [statusFilter, setStatusFilter] = useEinstellung<string>('releases.status', '');

  const { data, isLoading } = useQuery({
    queryKey: ['releases', { status: statusFilter }],
    queryFn: () => releases.list({ status: statusFilter || undefined }),
  });

  const { data: appsList } = useQuery({
    queryKey: ['applications'],
    queryFn: () => applications.list(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: Partial<ReleaseForm>) => releases.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['releases'] });
      setShowModal(false);
      setForm(DEFAULT_FORM);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to create release.';
      setFormError(msg);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => releases.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });

  const rolloutMutation = useMutation({
    mutationFn: (id: string) => releases.rollout(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });

  const rows = data ?? [];

  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    { key: 'produkt', label: 'Application', wert: (r) => r.application?.name ?? r.product?.name ?? '' },
    { key: 'version', label: t('releases.version'), wert: (r) => r.version },
    { key: 'name', label: t('common.name'), wert: (r) => r.releaseName ?? '' },
    { key: 'kanal', label: t('releases.channel'), wert: (r) => r.releaseChannel ?? '' },
    { key: 'status', label: t('common.status'), wert: (r) => r.status },
    { key: 'migration', label: t('releases.migration'), wert: (r) => (r.databaseMigrationRequired ? 1 : 0) },
    { key: 'bruch', label: t('releases.breaking'), wert: (r) => (r.breakingChange ? 1 : 0) },
    { key: 'erstellt', label: t('common.createdAt'), wert: (r) => r.createdAt },
    { key: 'freigabe', label: t('releases.approvedBy'), wert: (r) => r.approvedBy ?? '' },
  ];

  const tabelle = useTabelle({
    speichern: 'releases',
    zeilen: rows,
    spalten,
    startSortierung: { key: 'erstellt', richtung: 'ab' },
  });

  const RELEASE_STATUSES: ReleaseStatus[] = [
    'DRAFT', 'TESTING', 'APPROVED', 'SCHEDULED', 'ROLLING_OUT',
    'PARTIALLY_DEPLOYED', 'DEPLOYED', 'FAILED', 'ROLLED_BACK', 'DEPRECATED',
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{t('filters.allStatusesEn')}</option>
          {RELEASE_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500">{rows.length} releases</span>
        <button
          onClick={() => { setShowModal(true); setFormError(''); }}
          className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg"
        >
          <Plus size={15} /> New Release
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} extra={[t('common.actions')]} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 10 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-slate-400 italic">{t('releases.none')}</td>
              </tr>
            ) : (
              tabelle.sichtbar.map((rel) => (
                <tr key={rel.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 text-xs">{rel.application?.name ?? rel.product?.name ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-800">{rel.version}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{rel.releaseName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${rel.releaseChannel === 'stable' ? 'bg-emerald-100 text-emerald-700' : rel.releaseChannel === 'beta' ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-600'}`}>
                      {rel.releaseChannel}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={rel.status} type="release" /></td>
                  <td className="px-4 py-3 text-center">
                    {rel.databaseMigrationRequired ? (
                      <span className="text-amber-600 text-xs font-medium">Yes</span>
                    ) : (
                      <span className="text-slate-400 text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {rel.breakingChange ? (
                      <span className="text-red-600 text-xs font-medium">Yes</span>
                    ) : (
                      <span className="text-slate-400 text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {formatDistanceToNow(new Date(rel.createdAt), { addSuffix: true })}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{rel.approvedBy ?? '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {rel.status === 'TESTING' && (
                        <button
                          onClick={() => approveMutation.mutate(rel.id)}
                          disabled={approveMutation.isPending}
                          className="inline-flex items-center gap-0.5 text-xs text-emerald-600 hover:text-emerald-800 font-medium disabled:opacity-50"
                        >
                          <CheckCircle size={12} /> Approve
                        </button>
                      )}
                      {rel.status === 'APPROVED' && (
                        <button
                          onClick={() => {
                            if (confirm(`Rollout release ${rel.version}?`)) rolloutMutation.mutate(rel.id);
                          }}
                          disabled={rolloutMutation.isPending}
                          className="inline-flex items-center gap-0.5 text-xs text-brand-600 hover:text-brand-800 font-medium disabled:opacity-50"
                        >
                          <Rocket size={12} /> Rollout
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste blaettern={tabelle.blaettern} />
      {/* New Release Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-xl max-h-screen overflow-y-auto">
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface">
              <h2 className="text-base font-semibold">{t('releases.new')}</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFormError('');
                createMutation.mutate(form);
              }}
              className="px-6 py-5 space-y-4"
            >
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{formError}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Application *</label>
                  <select
                    required
                    value={form.applicationId}
                    onChange={(e) => setForm((f) => ({ ...f, applicationId: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Application wählen …</option>
                    {appsList?.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500">Das Release gilt für alle Instanzen dieser Application, auch für die aus Bündeln.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.versionRequired')}</label>
                  <input required placeholder="1.2.3" value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('releases.name')}</label>
                  <input value={form.releaseName} onChange={(e) => setForm((f) => ({ ...f, releaseName: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Channel</label>
                  <select value={form.releaseChannel} onChange={(e) => setForm((f) => ({ ...f, releaseChannel: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    <option value="stable">stable</option>
                    <option value="beta">beta</option>
                    <option value="nightly">nightly</option>
                  </select>
                </div>
                {/* Images aus der Vorlage des Produkts.
                    Vorher standen hier drei feste Felder — Backend, Frontend
                    und (seit heute) das dritte. Kommt ein Container dazu, ist
                    er damit im Formular, ohne dass jemand Code anfasst. */}
                <div className="col-span-2">
                  <ReleaseBilder
                    applicationId={form.applicationId}
                    werte={form.imageTags}
                    onChange={(schluessel, wert) =>
                      setForm((f) => {
                        const naechste = { ...f.imageTags };
                        if (wert) naechste[schluessel] = wert;
                        else delete naechste[schluessel];
                        return { ...f, imageTags: naechste };
                      })
                    }
                  />
                </div>

                <div className="col-span-2 flex gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.databaseMigrationRequired}
                      onChange={(e) => setForm((f) => ({ ...f, databaseMigrationRequired: e.target.checked }))}
                      className="rounded" />
                    DB Migration Required
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.breakingChange}
                      onChange={(e) => setForm((f) => ({ ...f, breakingChange: e.target.checked }))}
                      className="rounded" />
                    Breaking Change
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('releases.notes')}</label>
                  <textarea rows={4} value={form.releaseNotes} onChange={(e) => setForm((f) => ({ ...f, releaseNotes: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600">Cancel</button>
                <button type="submit" disabled={createMutation.isPending} className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60">
                  {createMutation.isPending ? 'Creating…' : 'Create Release'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
